import { useState, useEffect, useCallback } from 'react';
import type { DashboardSummary } from '@/types/dashboard';
import { t } from '@/i18n';
import { generateUUID } from '@/utils/uuid';
import './ImportModal.css';

interface ValidationResult {
  valid: boolean;
  parsed?: object;
  error?: string;
  dashboardInfo?: { uuid: string; title: string; sourceKey: string; updatedAt?: string };
  warning?: string;
}

interface ImportModalProps {
  onClose: () => void;
  onImport: (dashboard: object, mode: 'create' | 'replace') => Promise<void>;
  dashboards: DashboardSummary[];
}

export function ImportModal({ onClose, onImport, dashboards }: ImportModalProps) {
  const [jsonText, setJsonText] = useState('');
  const [mode, setMode] = useState<'create' | 'replace'>('create');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  const resetValidation = useCallback(() => {
    setValidationResult(null);
    setShowReplaceConfirm(false);
    setImportError(null);
  }, []);

  const handleValidate = useCallback(() => {
    setImportError(null);
    setShowReplaceConfirm(false);

    if (!jsonText.trim()) {
      setValidationResult({ valid: false, error: t('dashboard.importJsonEmpty') });
      return;
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setValidationResult({ valid: false, error: t('dashboard.importJsonInvalid') + ': ' + msg });
      return;
    }

    const REQUIRED_FIELDS: Array<{ key: string; check: (v: unknown) => boolean }> = [
      { key: 'sourceKey', check: (v) => typeof v === 'string' },
      { key: 'viewMode', check: (v) => typeof v === 'string' },
      { key: 'layout', check: (v) => typeof v === 'object' && v !== null && !Array.isArray(v) },
      { key: 'panels', check: (v) => Array.isArray(v) },
    ];

    const missing = REQUIRED_FIELDS
      .filter(({ key, check }) => !check(parsed[key]))
      .map(({ key }) => key);

    if (missing.length > 0) {
      setValidationResult({
        valid: false,
        error: t('dashboard.importJsonMissingFields').replace('{fields}', missing.join(', ')),
      });
      return;
    }

    const uuid = typeof parsed.uuid === 'string' ? parsed.uuid : undefined;
    const title = typeof parsed.title === 'string' ? parsed.title : '';
    const sourceKey = parsed.sourceKey as string;

    if (mode === 'replace') {
      if (!uuid) {
        setValidationResult({ valid: false, error: t('dashboard.importReplaceNoUuid') });
        return;
      }
      const existing = dashboards.find((d) => d.uuid === uuid);
      if (existing) {
        const warningText = t('dashboard.importReplaceWarning')
          .replace('{title}', existing.title)
          .replace('{sourceKey}', existing.sourceKey)
          .replace('{date}', existing.updatedAt ?? '');
        setValidationResult({
          valid: true,
          parsed: parsed as object,
          dashboardInfo: {
            uuid,
            title: existing.title,
            sourceKey: existing.sourceKey,
            updatedAt: existing.updatedAt ?? undefined,
          },
          warning: warningText,
        });
      } else {
        setValidationResult({ valid: false, error: t('dashboard.importUuidNotFound').replace('{uuid}', uuid) });
      }
    } else {
      if (uuid && dashboards.some((d) => d.uuid === uuid)) {
        setValidationResult({
          valid: true,
          parsed: parsed as object,
          dashboardInfo: { uuid, title, sourceKey },
          warning: t('dashboard.importUuidAlreadyExists').replace('{uuid}', uuid),
        });
      } else {
        setValidationResult({ valid: true, parsed: parsed as object });
      }
    }
  }, [jsonText, mode, dashboards]);

  const handleImport = useCallback(async () => {
    if (!validationResult?.valid || !validationResult.parsed) return;

    if (mode === 'replace' && validationResult.warning && !showReplaceConfirm) {
      setShowReplaceConfirm(true);
      return;
    }

    let dataToImport: Record<string, unknown> = validationResult.parsed as Record<string, unknown>;

    if (mode === 'create') {
      const parsedUuid = typeof dataToImport.uuid === 'string' ? dataToImport.uuid : undefined;
      if (parsedUuid && dashboards.some((d) => d.uuid === parsedUuid)) {
        dataToImport = { ...dataToImport, uuid: generateUUID() };
      }
    }

    setIsImporting(true);
    setImportError(null);

    try {
      await onImport(dataToImport, mode);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setImportError(msg);
    } finally {
      setIsImporting(false);
    }
  }, [validationResult, mode, showReplaceConfirm, dashboards, onImport]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const canImport = validationResult?.valid && !isImporting;

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      role="dialog"
      aria-label={t('dashboard.importTitle')}
      aria-modal="true"
      data-testid="import-modal"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="las la-file-import me-2" />
              {t('dashboard.importTitle')}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label={t('dashboard.importCancel')}
              onClick={onClose}
              disabled={isImporting}
            />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <textarea
                className="import-modal__textarea form-control"
                data-testid="import-modal-textarea"
                value={jsonText}
                onChange={(e) => {
                  setJsonText(e.target.value);
                  resetValidation();
                }}
                placeholder={t('dashboard.importPasteJson')}
                disabled={isImporting}
                rows={8}
              />
            </div>

            <div className="import-modal__mode-selector mb-3">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  id="import-mode-create"
                  value="create"
                  checked={mode === 'create'}
                  onChange={() => {
                    setMode('create');
                    resetValidation();
                  }}
                  disabled={isImporting}
                />
                <label className="form-check-label" htmlFor="import-mode-create">
                  {t('dashboard.importModeCreate')}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  id="import-mode-replace"
                  value="replace"
                  checked={mode === 'replace'}
                  onChange={() => {
                    setMode('replace');
                    resetValidation();
                  }}
                  disabled={isImporting}
                />
                <label className="form-check-label" htmlFor="import-mode-replace">
                  {t('dashboard.importModeReplace')}
                </label>
              </div>
            </div>

            {showReplaceConfirm && (
              <div className="import-modal__replace-confirm alert alert-warning" role="alert">
                <i className="las la-exclamation-triangle me-2" />
                {t('dashboard.importReplaceConfirmMessage')}
              </div>
            )}

            {importError && (
              <div className="alert alert-danger" role="alert">
                <i className="las la-times-circle me-2" />
                {importError}
              </div>
            )}

            {validationResult && (
              <div className="import-modal__validation-result">
                {validationResult.valid ? (
                  <div className="import-modal__validation-success">
                    <i className="las la-check-circle me-1" />
                    {t('dashboard.importJsonValid')}
                  </div>
                ) : (
                  <div className="import-modal__validation-error">
                    <i className="las la-times-circle me-1" />
                    {validationResult.error}
                  </div>
                )}
                {validationResult.warning && (
                  <div className="import-modal__validation-warning">
                    <i className="las la-exclamation-triangle me-1" />
                    {validationResult.warning}
                    {validationResult.dashboardInfo && (
                      <div className="small mt-1">
                        {t('dashboard.importDashboardInfo')
                          .replace('{uuid}', validationResult.dashboardInfo.uuid)
                          .replace('{title}', validationResult.dashboardInfo.title)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isImporting}
            >
              {t('dashboard.importCancel')}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleValidate}
              disabled={isImporting}
              data-testid="import-modal-validate"
            >
              <i className="las la-search me-1" />
              {t('dashboard.importValidate')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleImport}
              disabled={!canImport}
              data-testid="import-modal-import"
            >
              {isImporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  {t('dashboard.importImporting')}
                </>
              ) : (
                <>
                  <i className="las la-file-import me-1" />
                  {t('dashboard.importButton')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop show"
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1 }}
      />
    </div>
  );
}
